import React, { useState } from "react";

import { es } from "date-fns/locale";
import dayjs from "dayjs";
import { CalendarDays } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Calendar } from "~/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";

interface EmissionDateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the chosen emission date as YYYY-MM-DD. */
  onConfirm: (emissionDate: string) => void;
}

/**
 * Superadmin-only picker for emitting a factura with a back-dated emission
 * date. The selectable window — 1st of the previous calendar month through
 * today — mirrors the server-side validation in the create-factura edge
 * function.
 */
export const EmissionDateDialog: React.FC<EmissionDateDialogProps> = ({
  open,
  onOpenChange,
  onConfirm,
}) => {
  const today = new Date();
  const lowerBound = dayjs().subtract(1, "month").startOf("month").toDate();

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(today);

  const handleConfirm = () => {
    if (!selectedDate) return;
    onConfirm(dayjs(selectedDate).format("YYYY-MM-DD"));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[380px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-green-600" />
            Fecha de emisión
          </DialogTitle>
          <DialogDescription>
            Selecciona la fecha con la que se emitirá la factura. Solo se
            permite desde el 1° del mes anterior hasta hoy.
          </DialogDescription>
        </DialogHeader>

        <div className="flex justify-center py-2">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={setSelectedDate}
            disabled={[{ before: lowerBound }, { after: today }]}
            defaultMonth={selectedDate ?? today}
            initialFocus
            locale={es}
            className="rounded-md border"
          />
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-muted-foreground">
            {selectedDate ? (
              <>
                Emitir con fecha{" "}
                <span className="font-medium text-foreground">
                  {dayjs(selectedDate).format("DD/MM/YYYY")}
                </span>
              </>
            ) : (
              "Selecciona una fecha"
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={!selectedDate}
              className="bg-green-600 text-white hover:bg-green-700"
            >
              Generar factura
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
